import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useSalon } from "../context/SalonContext";
import { whatsappApi } from "../lib/whatsappApi";
import api, { auth } from "../lib/api";
import { useWhatsAppStatus } from "../hooks/useWhatsAppStatus";
import { consumeRouteHint, returnHomeIfRequested, shouldReturnHome } from "../lib/setupRoute";

// ─── Helpers ────────────────────────────────────────────────────────────
const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtPhone = (raw) => {
    const d = String(raw || "").replace(/\D/g, "");
    if (d.length === 13) return `+${d.slice(0,2)} (${d.slice(2,4)}) ${d.slice(4,9)}-${d.slice(9)}`;
    if (d.length === 12) return `+${d.slice(0,2)} (${d.slice(2,4)}) ${d.slice(4,8)}-${d.slice(8)}`;
    if (d.length === 11) return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
    if (d.length === 10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
    return raw || "";
};
const initials = (name) => (name || "?").trim().split(/\s+/).slice(0,2).map(w => w[0]?.toUpperCase()).join("");
const timeAgo = (dt) => {
    if (!dt) return "";
    const t = typeof dt === "string" ? new Date(dt) : dt;
    const diff = Math.floor((Date.now() - t.getTime()) / 1000);
    if (diff < 60) return "agora";
    if (diff < 3600) return `${Math.floor(diff/60)}min`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h`;
    return `${Math.floor(diff/86400)}d`;
};
const applyVars = (tpl, ctx) => {
    let out = tpl || "";
    Object.entries(ctx || {}).forEach(([k, v]) => {
        out = out.replaceAll(`{${k}}`, String(v ?? ""));
    });
    return out;
};

// ─── Section shell ──────────────────────────────────────────────────────
const SectionShell = ({ title, subtitle, onBack, children, actions }) => (
    <div className="space-y-5" data-testid={`whatsapp-section-${title?.toLowerCase()}`}>
        <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
                <button
                    data-testid="whatsapp-back-btn"
                    onClick={onBack}
                    className="h-10 w-10 rounded-xl border border-white/10 hover:border-emerald-500/40 text-slate-400 hover:text-emerald-300 flex items-center justify-center transition-all"
                >
                    <i className="fa-solid fa-arrow-left"></i>
                </button>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">WhatsApp</div>
                    <h2 className="salon-heading text-2xl text-white">{title}</h2>
                    {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
                </div>
            </div>
            {actions}
        </div>
        {children}
    </div>
);

// ─── Section 1 · Conexão ────────────────────────────────────────────────
const ConexaoSection = ({ onBack, status, refresh }) => {
    const [qr, setQr] = useState(null);
    const [qrProvider, setQrProvider] = useState(null);
    const [qrLoading, setQrLoading] = useState(false);
    const [stats, setStats] = useState(null);
    const [confirmDisconnect, setConfirmDisconnect] = useState(false);
    const [clearingSession, setClearingSession] = useState(false);
    const qrTimerRef = useRef(null);
    const loadingRef = useRef(false); // guard anti double-fire (StrictMode)

    const loadQr = async () => {
        if (loadingRef.current) return; // já tem uma chamada em voo
        loadingRef.current = true;
        setQrLoading(true);
        try {
            // Pré-check leve: valida sessão antes de pedir o QR.
            // Se o token expirou, o interceptor global limpa storage e força reload — não vamos chegar aqui.
            if (!auth.getToken()) {
                window.location.href = "/";
                return;
            }
            try {
                await api.get("/auth/me");
            } catch (meErr) {
                if (meErr.status === 401) return; // interceptor global cuidará do redirect + reload
                // Outros erros no /me não são fatais — segue o baile.
            }
            const r = await whatsappApi.getQrcode();
            if (r.connected) { setQr(null); refresh(); return; }
            setQr(r.qr_code);
            setQrProvider(r.provider || null);
        } catch (e) {
            if (e.status === 401) return; // interceptor cuidou
            toast.error("Erro ao gerar QR", { description: e.message });
        } finally {
            setQrLoading(false);
            loadingRef.current = false;
        }
    };

    // Auto-refresh QR + status polling quando desconectado
    useEffect(() => {
        if (status.connected) return;
        loadQr();
        qrTimerRef.current = setInterval(() => {
            loadQr();
            refresh();
        }, 15000);
        return () => qrTimerRef.current && clearInterval(qrTimerRef.current);
    }, [status.connected]);

    // Fast polling for status when QR is shown (every 3s)
    useEffect(() => {
        if (status.connected) return;
        const t = setInterval(refresh, 3000);
        return () => clearInterval(t);
    }, [status.connected, refresh]);

    // Load stats when connected
    useEffect(() => {
        if (!status.connected) return;
        whatsappApi.getStats().then(setStats).catch(() => {});
    }, [status.connected]);

    // Deep-linked from the Home setup checklist: once the WhatsApp connects,
    // celebrate briefly and bounce the user straight back to Home.
    useEffect(() => {
        if (!status.connected || !shouldReturnHome()) return;
        toast.success("WhatsApp conectado!", { description: "Tudo pronto — voltando para o início." });
        const t = setTimeout(onBack, 1600);
        return () => clearTimeout(t);
    }, [status.connected, onBack]);

    const doDisconnect = async () => {
        try {
            await whatsappApi.disconnect();
            toast.success("WhatsApp desconectado");
            setConfirmDisconnect(false);
            refresh();
        } catch (e) { toast.error("Erro ao desconectar", { description: e.message }); }
    };

    // Limpa sessão travada/antiga (credenciais velhas no Mongo que fazem o
    // Baileys ficar reconectando sozinho em loop) e busca um QR novo em seguida.
    const doClearSession = async () => {
        setClearingSession(true);
        try {
            await whatsappApi.clearSession();
            toast.success("Sessão limpa", { description: "Gerando um QR novo…" });
            setQr(null);
            setQrProvider(null);
            await loadQr();
            refresh();
        } catch (e) {
            toast.error("Erro ao limpar sessão", { description: e.message });
        } finally {
            setClearingSession(false);
        }
    };

    return (
        <SectionShell title="Conexão" subtitle="Autorize o WhatsApp do salão para ativar mensagens automáticas" onBack={onBack}>
            {status.connected ? (
                <div className="grid md:grid-cols-2 gap-4" data-testid="whatsapp-connected-panel">
                    <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6">
                        <div className="flex items-center gap-2 mb-3">
                            <span className="h-2.5 w-2.5 rounded-full bg-emerald-400 animate-pulse"></span>
                            <span className="text-[10px] uppercase tracking-[0.3em] text-emerald-300">Conectado</span>
                        </div>
                        <div className="text-white text-lg font-mono mb-1" data-testid="whatsapp-phone">{status.phone_number || "—"}</div>
                        <div className="text-xs text-slate-500">Conectado em {status.connected_at ? new Date(status.connected_at).toLocaleString("pt-BR") : "—"}</div>
                        <button
                            data-testid="whatsapp-disconnect-btn"
                            onClick={() => setConfirmDisconnect(true)}
                            className="mt-6 w-full rounded-xl border border-red-500/40 bg-red-500/10 hover:bg-red-500/20 text-red-300 py-2.5 text-sm font-medium transition-all"
                        >
                            <i className="fa-solid fa-plug-circle-xmark mr-2"></i>Desconectar WhatsApp
                        </button>
                    </div>
                    <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-6">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-3">Mensagens enviadas</div>
                        <div className="grid grid-cols-3 gap-4">
                            {[["sent_today","Hoje"],["sent_week","7 dias"],["sent_month","30 dias"]].map(([k, lbl]) => (
                                <div key={k}>
                                    <div className="text-3xl font-mono text-emerald-300" data-testid={`whatsapp-stat-${k}`}>{stats?.[k] ?? "—"}</div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">{lbl}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-8 md:p-10 flex flex-col md:flex-row gap-8 items-center" data-testid="whatsapp-qrcode-panel">
                    <div className="flex-1 min-w-0 space-y-3 text-center md:text-left">
                        <div className="h-14 w-14 rounded-2xl bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center mx-auto md:mx-0">
                            <i className="fa-brands fa-whatsapp text-3xl text-emerald-300"></i>
                        </div>
                        <h3 className="salon-heading text-2xl text-white">Conecte o WhatsApp do seu salão</h3>
                        <p className="text-sm text-slate-400 max-w-md">
                            Ative todas as mensagens automáticas: confirmações, lembretes 24h, reativação de clientes e campanhas em lote.
                        </p>
                        <ol className="text-xs text-slate-500 space-y-1.5 max-w-md">
                            <li><span className="text-amber-200 font-mono mr-1">1.</span> Abra o WhatsApp no celular.</li>
                            <li><span className="text-amber-200 font-mono mr-1">2.</span> Menu <span className="text-slate-300">Aparelhos conectados</span> → <span className="text-slate-300">Conectar um aparelho</span>.</li>
                            <li><span className="text-amber-200 font-mono mr-1">3.</span> Aponte a câmera para o QR ao lado.</li>
                        </ol>
                        {qrProvider === "unavailable" ? (
                            <div className="flex items-start gap-2 pt-2 text-[11px] text-red-300" data-testid="whatsapp-qr-unavailable-warning">
                                <i className="fa-solid fa-triangle-exclamation mt-0.5"></i>
                                <span>
                                    Serviço de WhatsApp indisponível no momento — aguardando ele voltar
                                    para gerar um QR real. Tentando de novo automaticamente…
                                </span>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 pt-2 text-[11px] text-amber-300/80">
                                <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse"></span>
                                Aguardando conexão…
                            </div>
                        )}
                    </div>
                    <div className="relative">
                        <div className="h-56 w-56 rounded-2xl bg-white p-3 shadow-[0_0_60px_-15px_rgba(16,185,129,0.4)]" data-testid="whatsapp-qrcode-img">
                            {qrLoading || !qr ? (
                                <div className="h-full w-full flex items-center justify-center text-slate-400">
                                    <div className="inline-block h-8 w-8 border-2 border-emerald-500 border-r-transparent rounded-full animate-spin"></div>
                                </div>
                            ) : (
                                <img
                                    src={qr}
                                    alt="QR Code WhatsApp"
                                    className="h-full w-full object-contain"
                                    style={{ imageRendering: "pixelated" }}
                                />
                            )}
                        </div>
                        <div className="text-center text-[10px] uppercase tracking-[0.3em] text-slate-600 mt-2">QR expira em 30s</div>
                        <button
                            data-testid="whatsapp-clear-session-btn"
                            onClick={doClearSession}
                            disabled={clearingSession}
                            title="Use se o QR não funcionar ou o WhatsApp ficar reconectando uma sessão antiga sozinho"
                            className="mt-3 w-full flex items-center justify-center gap-2 rounded-xl border border-white/10 hover:border-red-500/40 text-slate-500 hover:text-red-300 py-2 text-[11px] uppercase tracking-wider transition-all disabled:opacity-50"
                        >
                            {clearingSession ? (
                                <span className="inline-block h-3 w-3 border-2 border-current border-r-transparent rounded-full animate-spin"></span>
                            ) : (
                                <i className="fa-solid fa-broom"></i>
                            )}
                            Desconectar e limpar sessão
                        </button>
                    </div>
                </div>
            )}

            {confirmDisconnect && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-6" data-testid="whatsapp-disconnect-modal">
                    <div className="max-w-md w-full rounded-2xl border border-red-500/30 bg-slate-900 p-6">
                        <h3 className="salon-heading text-xl text-white mb-2">Desconectar WhatsApp?</h3>
                        <p className="text-sm text-slate-400 mb-5">As mensagens automáticas (lembretes, confirmações, reativações) serão desativadas até você reconectar.</p>
                        <div className="flex gap-2">
                            <button onClick={() => setConfirmDisconnect(false)} className="flex-1 rounded-xl border border-white/10 hover:border-white/20 text-slate-300 py-2.5 text-sm">Cancelar</button>
                            <button data-testid="whatsapp-disconnect-confirm" onClick={doDisconnect} className="flex-1 rounded-xl bg-red-500 hover:bg-red-400 text-white py-2.5 text-sm font-bold">Desconectar</button>
                        </div>
                    </div>
                </div>
            )}
        </SectionShell>
    );
};

// ─── Section 2 · Conversas ─────────────────────────────────────────────
const ConversasSection = ({ onBack, refresh }) => {
    const [convs, setConvs] = useState([]);
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [selectedPhone, setSelectedPhone] = useState(null);
    const [messages, setMessages] = useState([]);
    const [msgLoading, setMsgLoading] = useState(false);
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const msgEndRef = useRef(null);

    const loadConvs = async () => {
        try { setConvs(await whatsappApi.listConversations() || []); }
        catch (e) { /* silencioso */ }
    };
    useEffect(() => {
        loadConvs();
        const t = setInterval(loadConvs, 6000);
        return () => clearInterval(t);
    }, []);

    const loadMsgs = async (phone) => {
        setMsgLoading(true);
        try {
            const msgs = await whatsappApi.getConversation(phone);
            setMessages(msgs || []);
            refresh(); // atualiza contador não lidas
            setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
        } catch (e) { toast.error("Erro ao carregar mensagens"); }
        finally { setMsgLoading(false); }
    };
    useEffect(() => {
        if (selectedPhone) loadMsgs(selectedPhone);
    }, [selectedPhone]);

    const filtered = useMemo(() => {
        const s = search.trim().toLowerCase();
        return (convs || []).filter((c) => {
            if (filter === "unread" && (c.unread || 0) === 0) return false;
            if (filter === "clients" && !c.client_id) return false;
            if (s) {
                const inName = (c.client_name || "").toLowerCase().includes(s);
                const inPhone = String(c.phone || "").includes(s);
                if (!inName && !inPhone) return false;
            }
            return true;
        });
    }, [convs, filter, search]);

    const send = async () => {
        if (!draft.trim() || !selectedPhone) return;
        setSending(true);
        try {
            await whatsappApi.sendToPhone(selectedPhone, draft.trim());
            setDraft("");
            await loadMsgs(selectedPhone);
            await loadConvs();
        } catch (e) { toast.error("Erro ao enviar", { description: e.message }); }
        finally { setSending(false); }
    };

    return (
        <SectionShell title="Conversas" subtitle="Histórico e envio direto por número" onBack={onBack}>
            <div className="grid md:grid-cols-[340px_1fr] gap-4 h-[calc(100vh-260px)] min-h-[540px]" data-testid="whatsapp-conversations">
                {/* Lista */}
                <div className="rounded-2xl border border-white/10 bg-slate-900/40 flex flex-col overflow-hidden">
                    <div className="p-3 border-b border-white/5 space-y-2">
                        <div className="relative">
                            <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-500"></i>
                            <input
                                data-testid="whatsapp-conv-search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Buscar por nome ou número…"
                                className="w-full bg-slate-950/60 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm outline-none focus:border-emerald-500/40"
                            />
                        </div>
                        <div className="flex gap-1">
                            {[["all","Todas"],["unread","Não lidas"],["clients","Clientes"]].map(([k,l]) => (
                                <button
                                    key={k}
                                    data-testid={`whatsapp-filter-${k}`}
                                    onClick={() => setFilter(k)}
                                    className={`flex-1 text-[11px] uppercase tracking-[0.15em] px-2 py-1.5 rounded-md transition-all ${filter===k?"bg-emerald-500/15 text-emerald-200 border border-emerald-500/30":"text-slate-500 border border-transparent hover:text-slate-300"}`}
                                >{l}</button>
                            ))}
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        {filtered.length === 0 ? (
                            <div className="p-6 text-center text-xs text-slate-500">Nenhuma conversa encontrada.</div>
                        ) : filtered.map((c) => {
                            const active = selectedPhone === c.phone;
                            return (
                                <button
                                    key={c.phone}
                                    data-testid={`whatsapp-conv-${c.phone}`}
                                    onClick={() => setSelectedPhone(c.phone)}
                                    className={`w-full text-left px-3 py-3 border-b border-white/5 flex items-center gap-3 transition-all ${active?"bg-emerald-500/10":"hover:bg-slate-800/40"}`}
                                >
                                    <div className="h-10 w-10 rounded-full bg-slate-800 border border-white/10 flex items-center justify-center text-emerald-300 font-bold text-sm shrink-0">
                                        {initials(c.client_name || c.phone)}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-baseline justify-between gap-2">
                                            <div className="text-sm text-white truncate flex items-center gap-1">
                                                {c.client_name || fmtPhone(c.phone)}
                                                {c.client_id && <i className="fa-solid fa-user-check text-[9px] text-emerald-400"></i>}
                                            </div>
                                            <div className="text-[10px] text-slate-500 shrink-0">{timeAgo(c.last_ts)}</div>
                                        </div>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            <div className="text-xs text-slate-500 truncate flex-1">{c.last_direction==="out" && "Você: "}{c.last_message}</div>
                                            {c.unread > 0 && (
                                                <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-500 text-slate-950 text-[10px] font-bold flex items-center justify-center">{c.unread}</span>
                                            )}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Painel de conversa */}
                <div className="rounded-2xl border border-white/10 bg-slate-900/40 flex flex-col overflow-hidden">
                    {!selectedPhone ? (
                        <div className="flex-1 flex flex-col items-center justify-center text-slate-500 gap-2">
                            <i className="fa-brands fa-whatsapp text-6xl text-emerald-500/20"></i>
                            <div className="text-sm">Selecione uma conversa</div>
                        </div>
                    ) : (
                        <>
                            <div className="px-4 py-3 border-b border-white/5 flex items-center gap-3">
                                <div className="h-9 w-9 rounded-full bg-slate-800 border border-white/10 flex items-center justify-center text-emerald-300 text-xs font-bold">
                                    {initials((convs.find(c=>c.phone===selectedPhone)?.client_name) || selectedPhone)}
                                </div>
                                <div className="min-w-0">
                                    <div className="text-sm text-white truncate">{(convs.find(c=>c.phone===selectedPhone)?.client_name) || fmtPhone(selectedPhone)}</div>
                                    <div className="text-[10px] text-slate-500 font-mono">{fmtPhone(selectedPhone)}</div>
                                </div>
                            </div>
                            <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[radial-gradient(rgba(255,255,255,0.02)_1px,transparent_1px)] [background-size:18px_18px]">
                                {msgLoading ? (
                                    <div className="text-center text-xs text-slate-500 py-6">Carregando…</div>
                                ) : messages.length === 0 ? (
                                    <div className="text-center text-xs text-slate-500 py-6">Nenhuma mensagem trocada ainda.</div>
                                ) : messages.map((m) => (
                                    <div key={m.id} className={`flex ${m.direction==="out"?"justify-end":"justify-start"}`}>
                                        <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm shadow-sm ${m.direction==="out"?"bg-emerald-500/90 text-slate-900 rounded-br-sm":"bg-slate-800/80 text-slate-100 rounded-bl-sm"}`}>
                                            <div className="whitespace-pre-wrap">{m.message}</div>
                                            <div className={`text-[9px] mt-1 text-right ${m.direction==="out"?"text-emerald-900/60":"text-slate-500"}`}>
                                                {new Date(m.created_at).toLocaleTimeString("pt-BR", {hour:"2-digit",minute:"2-digit"})}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <div ref={msgEndRef} />
                            </div>
                            <div className="p-3 border-t border-white/5 flex gap-2">
                                <input
                                    data-testid="whatsapp-msg-input"
                                    value={draft}
                                    onChange={(e) => setDraft(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && !sending && send()}
                                    placeholder="Digite sua mensagem…"
                                    className="flex-1 bg-slate-950/60 border border-white/10 rounded-full px-4 py-2.5 text-sm outline-none focus:border-emerald-500/40"
                                />
                                <button
                                    data-testid="whatsapp-msg-send"
                                    onClick={send}
                                    disabled={sending || !draft.trim()}
                                    className="h-10 w-10 rounded-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-slate-950 flex items-center justify-center transition-all"
                                >
                                    <i className={`fa-solid ${sending?"fa-spinner fa-spin":"fa-paper-plane"} text-xs`}></i>
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </SectionShell>
    );
};

// ─── Section 3 · Envios ─────────────────────────────────────────────────
const EnviosSection = ({ onBack, templates, initialTemplateId, refresh }) => {
    const { clients, brand } = useSalon();
    const [mode, setMode] = useState("individual");

    // Individual
    const [selectedClient, setSelectedClient] = useState(null);
    const [searchClient, setSearchClient] = useState("");
    const [msg, setMsg] = useState("");
    const [pickedTemplate, setPickedTemplate] = useState(initialTemplateId || "");

    useEffect(() => {
        if (pickedTemplate) {
            const t = templates.find((x) => x.id === pickedTemplate);
            if (t) setMsg(t.message);
        }
    }, [pickedTemplate]);

    // Batch state
    const [batchStep, setBatchStep] = useState(1);
    const [segment, setSegment] = useState("all_active");
    const [customIds, setCustomIds] = useState([]);
    const [batchMsg, setBatchMsg] = useState("");
    const [batchTemplateId, setBatchTemplateId] = useState("");
    const [batchStatus, setBatchStatus] = useState(null); // {batch_id,total,sent,status}
    const batchTimerRef = useRef(null);

    useEffect(() => {
        if (batchTemplateId) {
            const t = templates.find((x) => x.id === batchTemplateId);
            if (t) setBatchMsg(t.message);
        }
    }, [batchTemplateId]);

    const clientsFiltered = useMemo(() => {
        const s = searchClient.trim().toLowerCase();
        if (!s) return (clients || []).slice(0, 20);
        return (clients || []).filter((c) =>
            (c.name || "").toLowerCase().includes(s) ||
            String(c.phone || "").includes(s)
        ).slice(0, 20);
    }, [clients, searchClient]);

    const recipientsPreviewCount = useMemo(() => {
        if (segment === "custom") return customIds.length;
        if (segment === "inactive_35d") {
            const cutoff = Date.now() - 35 * 86400000;
            return (clients || []).filter((c) => {
                const last = Math.max(0, ...(c.transactions||[]).map(t => t.date || 0));
                return c.active && (last === 0 || last < cutoff);
            }).length;
        }
        if (segment === "birthdays") {
            const m = new Date().getMonth() + 1;
            return (clients || []).filter((c) => {
                const bd = c.birthDate || c.birth_date;
                if (!bd) return false;
                const parts = bd.split("-");
                return parts.length === 3 && Number(parts[1]) === m;
            }).length;
        }
        return (clients || []).filter((c) => c.active !== false).length;
    }, [segment, customIds, clients]);

    const previewCtx = {
        nome: (selectedClient?.name) || "Cliente Teste",
        salao: brand.shopName || "Salão",
        data: new Date().toLocaleDateString("pt-BR"),
        hora: "15:00",
        link_agendamento: `kinng.app/${(brand.shopId||"salao")}/booking`,
    };

    const sendIndividual = async () => {
        if (!selectedClient || !msg.trim()) return;
        try {
            await whatsappApi.sendIndividual({
                phone: selectedClient.phone, message: applyVars(msg, {...previewCtx, nome: selectedClient.name}),
                client_id: selectedClient.id, template_id: pickedTemplate || null,
            });
            toast.success("Mensagem enviada", { description: selectedClient.name });
            setMsg(""); setPickedTemplate(""); setSelectedClient(null);
            refresh();
        } catch (e) { toast.error("Erro ao enviar", { description: e.message }); }
    };

    const submitBatch = async () => {
        try {
            const res = await whatsappApi.sendBatch({
                segment, client_ids: segment === "custom" ? customIds : [],
                message: batchMsg, template_id: batchTemplateId || null,
            });
            setBatchStatus({ ...res, sent: 0 });
            batchTimerRef.current = setInterval(async () => {
                try {
                    const p = await whatsappApi.batchProgress(res.batch_id);
                    setBatchStatus(p);
                    if (p.status === "done") {
                        clearInterval(batchTimerRef.current);
                        toast.success(`Envio concluído · ${p.sent}/${p.total}`);
                        refresh();
                    }
                } catch (_) { /* ignore */ }
            }, 800);
        } catch (e) { toast.error("Erro ao iniciar envio", { description: e.message }); }
    };
    useEffect(() => () => batchTimerRef.current && clearInterval(batchTimerRef.current), []);

    const resetBatch = () => {
        setBatchStep(1); setSegment("all_active"); setCustomIds([]);
        setBatchMsg(""); setBatchTemplateId(""); setBatchStatus(null);
    };

    return (
        <SectionShell title="Envios" subtitle="Individual ou em lote com templates" onBack={onBack}>
            <div className="inline-flex rounded-xl border border-white/10 p-1 bg-slate-900/40" data-testid="whatsapp-mode-toggle">
                {[["individual","Individual"],["batch","Em lote"]].map(([k,l]) => (
                    <button
                        key={k}
                        data-testid={`whatsapp-mode-${k}`}
                        onClick={() => setMode(k)}
                        className={`px-4 py-1.5 text-sm rounded-lg transition-all ${mode===k?"bg-emerald-500/20 text-emerald-200":"text-slate-400 hover:text-white"}`}
                    >{l}</button>
                ))}
            </div>

            {mode === "individual" && (
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-4 space-y-3">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">Cliente</div>
                            {selectedClient ? (
                                <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 flex items-center gap-3">
                                    <div className="h-9 w-9 rounded-full bg-slate-800 flex items-center justify-center text-emerald-300 text-xs font-bold">{initials(selectedClient.name)}</div>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-white truncate text-sm">{selectedClient.name}</div>
                                        <div className="text-xs text-slate-500 font-mono">{fmtPhone(selectedClient.phone)}</div>
                                    </div>
                                    <button onClick={() => setSelectedClient(null)} className="text-slate-400 hover:text-red-400 text-xs">Trocar</button>
                                </div>
                            ) : (
                                <>
                                    <input
                                        data-testid="whatsapp-individual-search"
                                        value={searchClient} onChange={(e) => setSearchClient(e.target.value)}
                                        placeholder="Nome ou telefone…"
                                        className="w-full bg-slate-950/60 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500/40"
                                    />
                                    <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-white/5">
                                        {clientsFiltered.length === 0 ? (
                                            <div className="p-3 text-xs text-slate-500 text-center">Nenhum cliente.</div>
                                        ) : clientsFiltered.map((c) => (
                                            <button
                                                key={c.id}
                                                data-testid={`whatsapp-pick-client-${c.id}`}
                                                onClick={() => setSelectedClient(c)}
                                                className="w-full text-left px-3 py-2 hover:bg-slate-800/50 border-b border-white/5 last:border-0 flex items-center gap-2"
                                            >
                                                <div className="text-sm text-white truncate flex-1">{c.name}</div>
                                                <div className="text-[11px] text-slate-500 font-mono">{fmtPhone(c.phone)}</div>
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>

                        <div>
                            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">Template (opcional)</div>
                            <select
                                data-testid="whatsapp-individual-template"
                                value={pickedTemplate} onChange={(e) => setPickedTemplate(e.target.value)}
                                className="w-full bg-slate-950/60 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500/40"
                            >
                                <option value="">— livre —</option>
                                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                        </div>

                        <div>
                            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">Mensagem</div>
                            <textarea
                                data-testid="whatsapp-individual-message"
                                value={msg} onChange={(e) => setMsg(e.target.value)}
                                rows={5}
                                placeholder="Digite… ou selecione um template. Use {nome}, {salao}, {hora}, {data}, {link_agendamento}"
                                className="w-full bg-slate-950/60 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500/40 resize-none"
                            />
                        </div>

                        <button
                            data-testid="whatsapp-individual-send"
                            onClick={sendIndividual}
                            disabled={!selectedClient || !msg.trim()}
                            className="w-full rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold py-2.5 text-sm disabled:opacity-40 transition-all"
                        >
                            <i className="fa-brands fa-whatsapp mr-2"></i>Enviar mensagem
                        </button>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-4">
                        <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-3">Prévia no celular</div>
                        <div className="mx-auto max-w-xs rounded-3xl border-4 border-slate-800 bg-slate-950 p-3 shadow-xl">
                            <div className="rounded-xl bg-[#0b141a] p-3 min-h-[280px] flex flex-col">
                                <div className="text-[10px] text-slate-500 mb-2 text-center">{brand.shopName || "Salão"}</div>
                                <div className="flex-1 flex items-end">
                                    <div className="max-w-[85%] bg-emerald-500/95 text-slate-900 px-3 py-2 rounded-2xl rounded-bl-sm text-xs">
                                        <div className="whitespace-pre-wrap">{applyVars(msg || "Sua mensagem aparecerá aqui…", {...previewCtx, nome: selectedClient?.name || previewCtx.nome})}</div>
                                        <div className="text-[9px] mt-1 text-right text-emerald-900/60">agora</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {mode === "batch" && (
                <div className="mt-4 space-y-4">
                    {batchStatus ? (
                        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6" data-testid="whatsapp-batch-progress">
                            <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 mb-2">Envio em andamento</div>
                            <div className="text-3xl font-mono text-white mb-3">{batchStatus.sent} / {batchStatus.total}</div>
                            <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                                <div className="h-full bg-emerald-400 transition-all" style={{width: `${(batchStatus.total?batchStatus.sent/batchStatus.total*100:0)}%`}}></div>
                            </div>
                            {batchStatus.truncated && (
                                <div className="mt-3 text-[11px] text-amber-300">
                                    <i className="fa-solid fa-circle-info mr-1"></i>
                                    Lote reduzido de <b>{batchStatus.total_all}</b> para <b>{batchStatus.limit || 50}</b> contatos (anti-ban).
                                </div>
                            )}
                            <div className="mt-4 text-xs text-slate-400">Status: <span className="text-emerald-300">{batchStatus.status}</span></div>
                            {batchStatus.status === "done" && (
                                <button onClick={resetBatch} className="mt-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-4 py-2 text-sm">Novo envio</button>
                            )}
                        </div>
                    ) : (
                        <>
                            {/* Stepper */}
                            <div className="flex items-center gap-2 flex-wrap">
                                {["Segmento","Mensagem","Preview","Confirmar"].map((lbl, i) => (
                                    <div key={lbl} className={`flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] ${batchStep>=i+1?"text-emerald-300":"text-slate-600"}`}>
                                        <span className={`h-5 w-5 rounded-full text-[10px] font-bold flex items-center justify-center ${batchStep>=i+1?"bg-emerald-500 text-slate-950":"bg-slate-800 text-slate-500"}`}>{i+1}</span>
                                        {lbl}
                                    </div>
                                ))}
                            </div>

                            <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-5">
                                {batchStep === 1 && (
                                    <div className="space-y-3" data-testid="whatsapp-batch-step-1">
                                        <div className="text-sm text-slate-300 mb-2">Quem vai receber?</div>
                                        {[["all_active","Todos os clientes ativos"],["inactive_35d","Inativos há +35 dias"],["birthdays","Aniversariantes do mês"],["custom","Seleção manual"]].map(([k,l]) => (
                                            <button
                                                key={k}
                                                data-testid={`whatsapp-batch-seg-${k}`}
                                                onClick={() => setSegment(k)}
                                                className={`w-full text-left rounded-xl border p-3 flex items-center justify-between transition-all ${segment===k?"border-emerald-500/50 bg-emerald-500/10":"border-white/10 hover:border-emerald-500/30"}`}
                                            >
                                                <span className={segment===k?"text-white":"text-slate-300"}>{l}</span>
                                                {segment===k && <i className="fa-solid fa-check text-emerald-300"></i>}
                                            </button>
                                        ))}
                                        {segment === "custom" && (
                                            <div className="mt-3 max-h-60 overflow-y-auto rounded-lg border border-white/5 bg-slate-950/50">
                                                {(clients||[]).map((c) => {
                                                    const on = customIds.includes(c.id);
                                                    return (
                                                        <button
                                                            key={c.id}
                                                            data-testid={`whatsapp-batch-client-${c.id}`}
                                                            onClick={() => setCustomIds((prev) => on ? prev.filter(x=>x!==c.id) : [...prev, c.id])}
                                                            className={`w-full text-left px-3 py-2 border-b border-white/5 last:border-0 flex items-center gap-2 ${on?"bg-emerald-500/10":""}`}
                                                        >
                                                            <span className={`h-4 w-4 rounded border flex items-center justify-center text-[9px] ${on?"bg-emerald-500 border-emerald-400 text-slate-950":"border-white/20"}`}>{on && <i className="fa-solid fa-check"></i>}</span>
                                                            <span className="text-sm text-white flex-1 truncate">{c.name}</span>
                                                            <span className="text-[11px] text-slate-500 font-mono">{fmtPhone(c.phone)}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        <div className="rounded-lg bg-emerald-500/5 border border-emerald-500/20 px-3 py-2 text-xs text-emerald-200" data-testid="whatsapp-batch-count">
                                            <i className="fa-solid fa-users mr-2"></i>{recipientsPreviewCount} cliente(s) selecionado(s)
                                        </div>
                                    </div>
                                )}

                                {batchStep === 2 && (
                                    <div className="space-y-3" data-testid="whatsapp-batch-step-2">
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">Template</div>
                                            <select
                                                value={batchTemplateId} onChange={(e) => setBatchTemplateId(e.target.value)}
                                                className="w-full bg-slate-950/60 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500/40"
                                            >
                                                <option value="">— livre —</option>
                                                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">Mensagem</div>
                                            <textarea
                                                data-testid="whatsapp-batch-message"
                                                value={batchMsg} onChange={(e) => setBatchMsg(e.target.value)}
                                                rows={6}
                                                placeholder="Use {nome}, {salao}, {data}, {hora}, {link_agendamento}"
                                                className="w-full bg-slate-950/60 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500/40 resize-none"
                                            />
                                        </div>
                                        <div className="text-[11px] text-slate-500">
                                            Variáveis: <code className="text-amber-200">{"{nome}"}</code> <code className="text-amber-200">{"{salao}"}</code> <code className="text-amber-200">{"{data}"}</code> <code className="text-amber-200">{"{hora}"}</code> <code className="text-amber-200">{"{link_agendamento}"}</code>
                                        </div>
                                    </div>
                                )}

                                {batchStep === 3 && (
                                    <div data-testid="whatsapp-batch-step-3">
                                        <div className="text-sm text-slate-400 mb-3">Prévia com variáveis substituídas:</div>
                                        <div className="mx-auto max-w-xs rounded-3xl border-4 border-slate-800 bg-slate-950 p-3">
                                            <div className="rounded-xl bg-[#0b141a] p-3 min-h-[220px]">
                                                <div className="max-w-[85%] bg-emerald-500/95 text-slate-900 px-3 py-2 rounded-2xl rounded-bl-sm text-xs">
                                                    <div className="whitespace-pre-wrap">{applyVars(batchMsg || "…", previewCtx)}</div>
                                                    <div className="text-[9px] mt-1 text-right text-emerald-900/60">agora</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {batchStep === 4 && (
                                    <div className="space-y-3" data-testid="whatsapp-batch-step-4">
                                        <div className="text-sm text-slate-300 mb-2">Confirmar envio</div>
                                        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4 space-y-1 text-sm">
                                            <div className="flex justify-between"><span className="text-slate-500">Segmento</span><span>{ {all_active:"Ativos", inactive_35d:"Inativos +35d", birthdays:"Aniversariantes", custom:"Manual"}[segment] }</span></div>
                                            <div className="flex justify-between"><span className="text-slate-500">Destinatários</span><span className="font-mono text-emerald-300">{recipientsPreviewCount}</span></div>
                                            <div className="flex justify-between"><span className="text-slate-500">Template</span><span>{templates.find(t=>t.id===batchTemplateId)?.name || "livre"}</span></div>
                                        </div>
                                        {recipientsPreviewCount > 50 && (
                                            <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200 flex gap-2" data-testid="whatsapp-batch-anti-ban-warning">
                                                <i className="fa-solid fa-triangle-exclamation mt-0.5 shrink-0"></i>
                                                <div>
                                                    <div className="font-medium mb-0.5">Lote grande detectado</div>
                                                    Por segurança do seu número, o envio é <b>limitado a 50 contatos</b> por batch e cada mensagem sai com <b>delay aleatório de 3–7s</b>. Isso reduz o risco de bloqueio pelo WhatsApp.
                                                </div>
                                            </div>
                                        )}
                                        <div className="rounded-xl border border-white/10 bg-slate-950/50 p-3 text-xs text-slate-400 whitespace-pre-wrap">{batchMsg}</div>
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center justify-between">
                                <button onClick={() => setBatchStep((s) => Math.max(1, s-1))} disabled={batchStep===1} className="rounded-lg border border-white/10 text-slate-300 hover:text-white px-4 py-2 text-sm disabled:opacity-30">Voltar</button>
                                {batchStep < 4 ? (
                                    <button
                                        data-testid="whatsapp-batch-next"
                                        onClick={() => setBatchStep((s) => s+1)}
                                        disabled={(batchStep===1 && recipientsPreviewCount===0) || (batchStep===2 && !batchMsg.trim())}
                                        className="rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-5 py-2 text-sm disabled:opacity-40"
                                    >Continuar<i className="fa-solid fa-arrow-right ml-2"></i></button>
                                ) : (
                                    <button
                                        data-testid="whatsapp-batch-submit"
                                        onClick={submitBatch}
                                        className="rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-5 py-2 text-sm"
                                    >Enviar para {recipientsPreviewCount} cliente(s)<i className="fa-solid fa-paper-plane ml-2"></i></button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}
        </SectionShell>
    );
};

// ─── Section 4 · Templates ─────────────────────────────────────────────
const TEMPLATE_TYPE_STYLE = {
    reativacao: { bg: "bg-amber-500/15 text-amber-200 border-amber-500/30", label: "Reativação" },
    agendamento: { bg: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30", label: "Confirmação" },
    lembrete: { bg: "bg-sky-500/15 text-sky-200 border-sky-500/30", label: "Lembrete" },
    boas_vindas: { bg: "bg-violet-500/15 text-violet-200 border-violet-500/30", label: "Boas-vindas" },
    promocao: { bg: "bg-pink-500/15 text-pink-200 border-pink-500/30", label: "Promoção" },
    custom: { bg: "bg-slate-500/15 text-slate-200 border-slate-500/30", label: "Custom" },
};

const TemplatesSection = ({ onBack, templates, reload, onUseInSend }) => {
    const [editing, setEditing] = useState(null); // template or {} for new

    const save = async () => {
        try {
            if (!editing.name?.trim() || !editing.message?.trim()) {
                toast.error("Preencha nome e mensagem"); return;
            }
            if (editing.id) {
                await whatsappApi.updateTemplate(editing.id, { name: editing.name, type: editing.type, message: editing.message });
                toast.success("Template atualizado");
            } else {
                await whatsappApi.createTemplate({ name: editing.name, type: editing.type || "custom", message: editing.message });
                toast.success("Template criado");
            }
            setEditing(null); reload();
        } catch (e) { toast.error("Erro", { description: e.message }); }
    };

    const del = async (t) => {
        if (!window.confirm(`Excluir template "${t.name}"?`)) return;
        try { await whatsappApi.deleteTemplate(t.id); toast.success("Excluído"); reload(); }
        catch (e) { toast.error("Erro", { description: e.message }); }
    };

    return (
        <SectionShell
            title="Templates"
            subtitle="Modelos de mensagem reutilizáveis"
            onBack={onBack}
            actions={
                <button
                    data-testid="whatsapp-template-new"
                    onClick={() => setEditing({ name: "", type: "custom", message: "" })}
                    className="rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-4 py-2.5 text-sm"
                >
                    <i className="fa-solid fa-plus mr-2"></i>Novo template
                </button>
            }
        >
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="whatsapp-templates-grid">
                {templates.length === 0 ? (
                    <div className="col-span-full text-center py-10 text-sm text-slate-500 border border-dashed border-white/10 rounded-2xl">
                        Nenhum template ainda.
                    </div>
                ) : templates.map((t) => {
                    const style = TEMPLATE_TYPE_STYLE[t.type] || TEMPLATE_TYPE_STYLE.custom;
                    return (
                        <div key={t.id} className="rounded-2xl border border-white/10 bg-slate-900/40 p-4 space-y-2" data-testid={`whatsapp-template-${t.id}`}>
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                    <div className="text-white font-medium truncate">{t.name}</div>
                                    <span className={`inline-block mt-1 text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full border ${style.bg}`}>{style.label}</span>
                                </div>
                            </div>
                            <div className="text-xs text-slate-400 line-clamp-3 whitespace-pre-wrap min-h-[3.5rem]">{t.message}</div>
                            <div className="flex items-center gap-1 pt-2 border-t border-white/5">
                                <button
                                    data-testid={`whatsapp-template-edit-${t.id}`}
                                    onClick={() => setEditing({...t})}
                                    className="text-[11px] text-slate-400 hover:text-white px-2 py-1"
                                >
                                    <i className="fa-solid fa-pen-to-square mr-1"></i>Editar
                                </button>
                                <button
                                    data-testid={`whatsapp-template-del-${t.id}`}
                                    onClick={() => del(t)}
                                    className="text-[11px] text-slate-400 hover:text-red-400 px-2 py-1"
                                >
                                    <i className="fa-solid fa-trash mr-1"></i>Excluir
                                </button>
                                <button
                                    data-testid={`whatsapp-template-use-${t.id}`}
                                    onClick={() => onUseInSend(t.id)}
                                    className="ml-auto text-[11px] text-emerald-300 hover:text-emerald-200 border border-emerald-500/30 rounded-lg px-2 py-1"
                                >
                                    Usar em Envio <i className="fa-solid fa-arrow-right ml-1"></i>
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {editing && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-6" data-testid="whatsapp-template-modal">
                    <div className="max-w-lg w-full rounded-2xl border border-white/10 bg-slate-950 p-6 space-y-3">
                        <h3 className="salon-heading text-xl text-white">{editing.id ? "Editar template" : "Novo template"}</h3>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">Nome</div>
                            <input value={editing.name} onChange={(e) => setEditing({...editing, name: e.target.value})} className="w-full bg-slate-900/60 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500/40" />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">Tipo</div>
                            <select value={editing.type} onChange={(e) => setEditing({...editing, type: e.target.value})} className="w-full bg-slate-900/60 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500/40">
                                {Object.entries(TEMPLATE_TYPE_STYLE).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">Mensagem</div>
                            <textarea value={editing.message} onChange={(e) => setEditing({...editing, message: e.target.value})} rows={6} className="w-full bg-slate-900/60 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500/40 resize-none" />
                            <div className="text-[10px] text-slate-500 mt-1">Use <code className="text-amber-200">{"{nome}"}</code>, <code className="text-amber-200">{"{salao}"}</code>, <code className="text-amber-200">{"{hora}"}</code>, <code className="text-amber-200">{"{data}"}</code>, <code className="text-amber-200">{"{link_agendamento}"}</code>.</div>
                        </div>
                        <div className="flex gap-2 pt-2">
                            <button onClick={() => setEditing(null)} className="flex-1 rounded-xl border border-white/10 text-slate-300 py-2.5 text-sm">Cancelar</button>
                            <button data-testid="whatsapp-template-save" onClick={save} className="flex-1 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold py-2.5 text-sm">Salvar</button>
                        </div>
                    </div>
                </div>
            )}
        </SectionShell>
    );
};

// ─── Root ───────────────────────────────────────────────────────────────
const WhatsApp = () => {
    const { setActiveModule } = useSalon();
    // hub | conexao | conversas | envios | templates — may be deep-linked from the Home setup checklist.
    const [view, setView] = useState(() => consumeRouteHint("kinng.route.whatsapp.view") || "hub");
    const [templates, setTemplates] = useState([]);
    const [preSelectedTemplate, setPreSelectedTemplate] = useState("");
    const { status, refresh } = useWhatsAppStatus(6000);

    const reloadTemplates = async () => {
        try { setTemplates(await whatsappApi.listTemplates() || []); }
        catch (_) { /* ignore */ }
    };
    useEffect(() => { reloadTemplates(); }, []);

    const useTemplateInSend = (id) => { setPreSelectedTemplate(id); setView("envios"); };

    // Back from Conexão returns to Home when the setup checklist sent the user here.
    const backFromConexao = useCallback(() => {
        if (!returnHomeIfRequested(() => setActiveModule("home"))) setView("hub");
    }, [setActiveModule]);

    if (view === "conexao") return <ConexaoSection onBack={backFromConexao} status={status} refresh={refresh} />;
    if (view === "conversas") return <ConversasSection onBack={() => setView("hub")} refresh={refresh} />;
    if (view === "envios") return <EnviosSection onBack={() => setView("hub")} templates={templates} initialTemplateId={preSelectedTemplate} refresh={refresh} />;
    if (view === "templates") return <TemplatesSection onBack={() => setView("hub")} templates={templates} reload={reloadTemplates} onUseInSend={useTemplateInSend} />;

    // Hub — 4 cards
    return (
        <div className="space-y-6" data-testid="whatsapp-hub">
            <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">WhatsApp</div>
                <h2 className="salon-heading text-3xl text-white">Central do WhatsApp</h2>
                <p className="text-sm text-slate-500 mt-1">Conexão, conversas, envios e templates — tudo num só lugar.</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Conexão */}
                <button
                    data-testid="whatsapp-card-conexao"
                    onClick={() => setView("conexao")}
                    className={`text-left rounded-3xl border p-5 transition-all group ${status.connected ? "border-emerald-500/30 bg-emerald-500/5 hover:border-emerald-500/60" : "border-amber-500/40 bg-amber-500/5 hover:border-amber-500/70"}`}
                >
                    <div className="flex items-center justify-between mb-4">
                        <div className={`h-12 w-12 rounded-2xl flex items-center justify-center ${status.connected ? "bg-emerald-500/15" : "bg-amber-500/15"}`}>
                            <i className="fa-solid fa-mobile-screen text-2xl text-emerald-300"></i>
                        </div>
                        <i className="fa-solid fa-arrow-right text-slate-600 group-hover:text-emerald-300 group-hover:translate-x-1 transition-all"></i>
                    </div>
                    <div className="text-white text-lg font-medium mb-1">Conexão</div>
                    {status.connected ? (
                        <div className="flex items-center gap-1.5 text-xs text-emerald-300"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse"></span>Conectado</div>
                    ) : (
                        <div className="flex items-center gap-1.5 text-xs text-amber-300"><i className="fa-solid fa-triangle-exclamation text-[10px]"></i>Desconectado</div>
                    )}
                </button>

                {/* Conversas */}
                <button
                    data-testid="whatsapp-card-conversas"
                    onClick={() => setView("conversas")}
                    className="text-left rounded-3xl border border-white/10 bg-slate-900/40 hover:border-emerald-500/40 p-5 transition-all group relative"
                >
                    <div className="flex items-center justify-between mb-4">
                        <div className="h-12 w-12 rounded-2xl bg-emerald-500/15 flex items-center justify-center">
                            <i className="fa-solid fa-comments text-2xl text-emerald-300"></i>
                        </div>
                        <i className="fa-solid fa-arrow-right text-slate-600 group-hover:text-emerald-300 group-hover:translate-x-1 transition-all"></i>
                    </div>
                    <div className="text-white text-lg font-medium mb-1">Conversas</div>
                    <div className="text-xs text-slate-500">
                        {status.unread > 0 ? <span className="text-emerald-300">{status.unread} não lidas</span> : "Todas em dia"}
                    </div>
                    {status.unread > 0 && (
                        <span className="absolute top-4 right-4 min-w-[22px] h-[22px] px-1.5 rounded-full bg-emerald-500 text-slate-950 text-xs font-bold flex items-center justify-center animate-pulse">
                            {status.unread > 99 ? "99+" : status.unread}
                        </span>
                    )}
                </button>

                {/* Envios */}
                <button
                    data-testid="whatsapp-card-envios"
                    onClick={() => setView("envios")}
                    className="text-left rounded-3xl border border-white/10 bg-slate-900/40 hover:border-emerald-500/40 p-5 transition-all group"
                >
                    <div className="flex items-center justify-between mb-4">
                        <div className="h-12 w-12 rounded-2xl bg-emerald-500/15 flex items-center justify-center">
                            <i className="fa-solid fa-paper-plane text-2xl text-emerald-300"></i>
                        </div>
                        <i className="fa-solid fa-arrow-right text-slate-600 group-hover:text-emerald-300 group-hover:translate-x-1 transition-all"></i>
                    </div>
                    <div className="text-white text-lg font-medium mb-1">Envios</div>
                    <div className="text-xs text-slate-500">Individual · Em lote</div>
                </button>

                {/* Templates */}
                <button
                    data-testid="whatsapp-card-templates"
                    onClick={() => setView("templates")}
                    className="text-left rounded-3xl border border-white/10 bg-slate-900/40 hover:border-emerald-500/40 p-5 transition-all group"
                >
                    <div className="flex items-center justify-between mb-4">
                        <div className="h-12 w-12 rounded-2xl bg-emerald-500/15 flex items-center justify-center">
                            <i className="fa-solid fa-file-lines text-2xl text-emerald-300"></i>
                        </div>
                        <i className="fa-solid fa-arrow-right text-slate-600 group-hover:text-emerald-300 group-hover:translate-x-1 transition-all"></i>
                    </div>
                    <div className="text-white text-lg font-medium mb-1">Templates</div>
                    <div className="text-xs text-slate-500">{templates.length} cadastrado{templates.length===1?"":"s"}</div>
                </button>
            </div>

            {/* Banner: alerta desconectado */}
            {!status.connected && !status.loading && (
                <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 flex items-center gap-4" data-testid="whatsapp-hub-disconnected-banner">
                    <i className="fa-solid fa-triangle-exclamation text-2xl text-amber-300"></i>
                    <div className="flex-1 min-w-0">
                        <div className="text-white font-medium">WhatsApp desconectado</div>
                        <div className="text-xs text-slate-400">Mensagens automáticas (lembretes, confirmações e campanhas) estão pausadas.</div>
                    </div>
                    <button
                        data-testid="whatsapp-hub-connect-cta"
                        onClick={() => setView("conexao")}
                        className="rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-4 py-2 text-sm whitespace-nowrap"
                    >
                        Conectar agora<i className="fa-solid fa-arrow-right ml-2"></i>
                    </button>
                </div>
            )}
        </div>
    );
};

export default WhatsApp;
